import { useEffect, useRef, useState } from "react";
import { useSpaceDisplayName } from "../../../hooks/useSpaceDisplayName";
import { useSpaces } from "../../../hooks/useSpaces";
import { spaceIcon } from "../../../lib/spaceIcon";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { Text } from "../../Text";
import { GeneratedKeyField } from "../GeneratedKeyField";
import {
  DropdownOpener,
  FieldLabel,
  ModalContent,
  ModalFooter,
} from "../shared";

export function CreateApiKeyModal({
  onClose,
  onCreateKey,
  showAccess = true,
  alert = false,
}: {
  onClose: () => void;
  onCreateKey: (name: string, spaceId: string | null) => Promise<string>;
  showAccess?: boolean;
  alert?: boolean;
}) {
  const displayName = useSpaceDisplayName();
  const [spaces] = useSpaces({ settingsView: true });
  const [name, setName] = useState("");
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // The dialog grabs focus on open (Modal calls el.focus()), so re-focus the
  // name field on the next frame so the user can start typing immediately.
  useEffect(() => {
    const id = requestAnimationFrame(() => nameRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  const nameReady = name.trim().length > 0;
  const generated = revealedKey !== null;

  const generate = async () => {
    if (!nameReady || generating || generated) return;
    setGenerating(true);
    const rawKey = await onCreateKey(name.trim(), spaceId).finally(() =>
      setGenerating(false)
    );
    setRevealedKey(rawKey);
  };

  const selectedSpaceName = spaceId
    ? (() => {
        const s = spaces.find((s) => s.id === spaceId);
        return s ? displayName(s) : "All spaces";
      })()
    : "All spaces";

  return (
    <Modal open onClose={onClose} alert={alert}>
      <ModalContent>
        <Text variant="text-regular-md">Create API key</Text>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Name</Text>
          </Box>
          <Input
            ref={nameRef}
            autoFocus
            disabled={generated}
            placeholder="e.g. Claude Code"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              minWidth: 0,
              width: "100%",
              ...(generated
                ? {
                    background: "var(--alpha2)",
                    color: "var(--text-secondary)",
                    WebkitTextFillColor: "var(--text-secondary)",
                  }
                : {}),
            }}
          />
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              A name to help you recognize this key later.
            </Text>
          </Box>
        </FieldLabel>

        {showAccess && (
          <FieldLabel>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">Access</Text>
            </Box>
            <Dropdown
              opener={
                <DropdownOpener>
                  <Text variant="text-regular" ellipsis>
                    {selectedSpaceName}
                  </Text>
                  <span style={{ opacity: 0.3 }}>
                    <Icon name="chevron-down" size={12} />
                  </span>
                </DropdownOpener>
              }
              align="bottom-left"
              sections={[
                {
                  items: [
                    {
                      type: "button" as const,
                      left: <Icon name="layers" size={16} />,
                      label: "All spaces",
                      right:
                        spaceId === null ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () => setSpaceId(null),
                    },
                    ...spaces.map((space) => ({
                      type: "button" as const,
                      left: <Icon name={spaceIcon(space.mode)} size={16} />,
                      label: displayName(space),
                      right:
                        space.id === spaceId ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () => setSpaceId(space.id),
                    })),
                  ],
                },
              ]}
            />
            <Box horizontal={8} top={6}>
              <Text variant="text-small" color="var(--text-secondary)">
                {spaceId
                  ? "This key can only reach workflows in the selected space."
                  : "This key can reach every space you’re a member of."}
              </Text>
            </Box>
          </FieldLabel>
        )}

        <GeneratedKeyField
          label="API key"
          generateLabel="Generate key"
          revealedKey={revealedKey}
          generating={generating}
          disabled={!nameReady}
          onGenerate={generate}
        />

        <div style={{ height: 8 }} />
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" onClick={onClose} disabled={!generated}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
